import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const CurifyYourCoreCurriculum = () => {
  const post = getBlogPost('curify-your-core-curriculum');
  return (
    <BlogArticle post={post}>
      <p>
        {
          'Engaging, student-centered instruction doesn’t require starting from scratch. With Curify, you can breathe new life into the materials you already use. Curipod’s Curify experience makes it easy to turn your core curriculum into interactive, student-centered learning experiences.'
        }
      </p>
      <p>
        <strong>
          {'Just upload your slides or materials. Click Curify. Feel the buzz.'}
        </strong>
      </p>
      <h2>{'From Static to Student-Centered with Curify'}</h2>
      <p>
        {
          'Core materials often fall short, rigid and built more for coverage than connection. The '
        }
        <strong>{'Curify experience'}</strong>
        {
          ' transforms those materials into student-centered lessons that are rigorous, responsive, and grounded in pedagogy.'
        }
      </p>
      <p>
        {'Rather than replace your curriculum, Curify helps you '}
        <strong>{'build on what you already have'}</strong>
        {', layering in research-based instructional strategies that support:'}
      </p>
      <ul>
        <li>{'Whole-class engagement'}</li>
        <li>{'Student voice'}</li>
        <li>
          <Link
            href="/c/blog-posts/why-feedback-fuels-learning-and-how-curipod-makes-it-easy"
            passHref
          >
            <a>{'Real-time feedback'}</a>
          </Link>
        </li>
        <li>{'Rigorous thinking and reflection'}</li>
        <li>{'Active movement and discussion'}</li>
      </ul>
      <p>
        {"Whether you're prepping for a state test or diving into a novel study, "}
        <strong>{'Curify blends the best of instructional design'}</strong>
        {'—i'}
        <Link
          href="/c/blog-posts/designing-for-all-learners-how-curipod-supports-udl-in-the-classroom"
          passHref
        >
          <a>{'ncluding UDL'}</a>
        </Link>
        {', '}
        <Link
          href="/c/blog-posts/make-thinking-visible-10-formative-assessments-that-center-student-voice"
          passHref
        >
          <a>{'formative assessment,'}</a>
        </Link>
        {'and classroom discourse—into every lesson you create.'}
      </p>
      <p>
        {
          '\\Whether you’re prepping for state tests or diving into a novel study, Curify helps you:'
        }
      </p>
      <ul>
        <li>{'Add meaningful activities (draw, vote, reflect)'}</li>
        <li>{'Align to standards or essential questions'}</li>
        <li>{'Keep lessons rigorous, responsive, and relevant'}</li>
      </ul>
      <p>
        <strong>
          {'The result? Lessons students remember—and teachers want to teach.'}
        </strong>
      </p>
      <h2>{'Curify in Action'}</h2>
      <p>
        {
          "Whether you're reviewing vocabulary, launching a novel study, or prepping for a test, "
        }
        <strong>{'Curify turns static slides into dynamic moments of learning'}</strong>
        {
          '. Instead of reinventing the wheel, teachers can simply upload what they already use—and watch the engagement multiply.'
        }
      </p>
      <p>{'Here are just a few ways educators are using Curify today:'}</p>
      <ul>
        <li>
          <strong>{'Upload slides'}</strong>
          {
            ' from your textbook unit and instantly add polls, drawing prompts, and short-answer reflections to spark discussion.'
          }
        </li>
        <li>
          <strong>{'Revamp a novel study'}</strong>
          {
            ' by embedding open-ended questions, interactive writing activities, and AI-powered feedback to elevate comprehension.'
          }
        </li>
        <li>
          <strong>
            {'Generate a lesson aligned to your standards or essential questions'}
          </strong>
          {' to reinforce key concepts through real-time checks for understanding.'}
        </li>
        <li>
          <strong>{'Transform a worksheet'}</strong>
          {
            ' into a collaborative experience where students explain their thinking, compare responses, and revise with support.'
          }
        </li>
        <li>
          <strong>{'Use test prep materials'}</strong>
          {
            ' as interactive exit tickets—so every student participates, reflects, and improves.'
          }
        </li>
      </ul>
      <p>
        <strong>{'Using the Bluebonnet Curriculum?'}</strong>
        {
          ' Curipod makes it easy to add write-pair-shares, live polls, and instant AI feedback—bringing your content to life while supporting student voice and classroom connection.'
        }
      </p>
      <p>
        {'No matter the content, '}
        <strong>
          {
            'Curify helps teachers meet their goals faster, without sacrificing engagement, rigor, or connection'
          }
        </strong>
        {
          '. And because it’s built on top of your existing materials, every lesson starts with what you know works and ends with 100% active participation.'
        }
      </p>
      <h2>{'Why Curify?'}</h2>
      <p>
        {
          'Because the gap isn’t always in the curriculum—it’s in how students experience it.'
        }
        <br />
        {
          "With Curify, you’re not scrapping your materials. You’re adapting them to students' needs as they are today."
        }
      </p>
      <p>
        <strong>
          {
            'Curify your curriculum. Spark curiosity. Drive connection. Make every lesson count.'
          }
        </strong>
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default CurifyYourCoreCurriculum;
